import { useState } from "react";
import { Edit2, MapPin, Mail, Phone, Building2, ChevronRight } from "lucide-react";
import ClientPicker from "../../clients/ClientPicker";

export default function BillToSectionPremium({ invoice, updateField, disabled = false, showValidation = false, type = "invoice" }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);

  const hasClient = invoice.client && invoice.client.id;
  const showError = showValidation && !hasClient;

  if (disabled && !invoice.client) {
    return null;
  }

  return (
    <section className="space-y-3" data-validation-target="client">
      {/* Client Selected - COMPACT PREMIUM LAYOUT */}
      {invoice.client ? (
        <div className="space-y-3">
          {/* Client Info - Inline Compact with DEPTH + HOVER LIFT */}
          <div className="group relative rounded-lg bg-white border border-slate-200/60 p-3 hover:border-indigo-200 hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200 cursor-pointer">
            {/* Subtle hover accent */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-t-lg" />
            
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 space-y-2">
                {/* Name + GST Badge */}
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
                  <h4 className="text-sm font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                    {invoice.client.name}
                  </h4>
                  {invoice.client.gstin && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 shadow-sm">
                      GST
                    </span>
                  )}
                </div>

                {/* Compact Info Grid */}
                <div className="grid grid-cols-1 gap-1.5 text-xs text-slate-600">
                  {invoice.client.email && (
                    <div className="flex items-center gap-1.5 group/item hover:text-indigo-600 transition-colors">
                      <Mail size={11} className="text-slate-400 group-hover/item:text-indigo-500" />
                      <span className="truncate">{invoice.client.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 group/item hover:text-indigo-600 transition-colors">
                    <MapPin size={11} className="text-slate-400 group-hover/item:text-indigo-500" />
                    <span className="truncate">
                      {invoice.client.city}
                      {invoice.client.state && `, ${invoice.client.state}`}
                      {invoice.client.pincode && ` ${invoice.client.pincode}`}
                    </span>
                  </div>
                  {invoice.client.phone && (
                    <div className="flex items-center gap-1.5 group/item hover:text-indigo-600 transition-colors">
                      <Phone size={11} className="text-slate-400 group-hover/item:text-indigo-500" />
                      <span>{invoice.client.phone}</span>
                    </div>
                  )}
                  {invoice.client.gstin && (
                    <div className="flex items-center gap-1.5 font-mono">
                      <span className="text-[10px] text-emerald-600 font-semibold">GSTIN</span>
                      <span className="text-emerald-700">{invoice.client.gstin}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Change Button - Subtle */}
              {!disabled && (
                <button
                  onClick={() => setPickerOpen(true)}
                  className="flex-shrink-0 text-xs font-semibold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 px-2.5 py-1.5 rounded-md transition-all duration-150 flex items-center gap-1 shadow-sm hover:shadow"
                >
                  Change
                  <ChevronRight size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Billing Address - Inline Editable with DEPTH */}
          <div className="group relative">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                Billing Address
              </span>
              {!editingAddress && !disabled && (
                <button
                  onClick={() => setEditingAddress(true)}
                  className="text-[11px] font-semibold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 px-2 py-0.5 rounded transition-all duration-150 flex items-center gap-1"
                >
                  <Edit2 size={10} />
                  Edit
                </button>
              )}
            </div>

            {editingAddress && !disabled ? (
              <textarea
                autoFocus
                rows={2}
                value={invoice.billingAddress}
                onChange={(e) => updateField("billingAddress", e.target.value)}
                onBlur={() => setEditingAddress(false)}
                className="w-full text-xs rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/30 border border-indigo-200 bg-white shadow-sm transition-all"
                placeholder="Enter billing address…"
              />
            ) : (
              <div className="text-xs rounded-lg px-3 py-2 bg-white border border-slate-100 text-slate-600 leading-relaxed hover:border-slate-200 hover:shadow-sm transition-all duration-200 cursor-text">
                {invoice.billingAddress || (
                  <span className="text-slate-400 italic">No billing address</span>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Empty State - Compact */
        <div className="space-y-2">
          <button
            onClick={() => setPickerOpen(true)}
            className={`w-full rounded-lg p-6 transition-all duration-200 ${
              showError 
                ? 'ring-2 ring-red-200 border-2 border-red-200 bg-red-50/30' 
                : 'border-2 border-dashed border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30'
            }`}
          >
            <div className={`mx-auto mb-2 w-10 h-10 rounded-full flex items-center justify-center ${
              showError ? 'bg-red-100' : 'bg-slate-100'
            }`}>
              <Building2 size={18} className={showError ? 'text-red-500' : 'text-slate-400'} />
            </div>

            <div className={`text-sm font-semibold ${
              showError ? 'text-red-700' : 'text-slate-900'
            }`}>
              Select client
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {type === "quotation" ? "Choose who this quotation is for" : "Choose who this invoice is billed to"}
            </div>
          </button>
          
          {showError && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100">
              <span className="text-red-600 text-sm">⚠</span>
              <p className="text-xs text-red-700">
                Select a client to continue. This invoice needs a billing recipient.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Client Picker */}
      {pickerOpen && (
        <ClientPicker
          onSelect={(client) => {
            updateField("client", client);
            updateField("billingAddress", formatClientAddress(client));
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </section>
  );
}

/* Utils */
function formatClientAddress(client) {
  if (!client) return "";
  return [
    client.address_line1,
    client.address_line2,
    client.city,
    client.state,
    client.pincode,
  ]
    .filter(Boolean)
    .join(", ");
}
